// Releases (portal overhaul O3). Admin: the release board (status chips,
// project filter, rows grouped by project) and a release's detail (path to
// live, sign-off, Changes / Tickets / Release notes). Client: sign-off hero
// cards for releases on sandbox, then Coming up and Released. Mirrors
// `showAdminReleaseBoard` / `showAdminRelease` / `showClientReleases` and the
// release dialog in the handoff prototype.
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  Field,
  Icon,
  IconButton,
  Input,
  Select,
  Tabs,
  type Tone,
} from "../../ds/DS";
import { api } from "../../lib/api";
import {
  FIXES_VERSION,
  type ChangeKind,
  type ReleaseStatus,
  type VProject,
  type VRelease,
  type VReleaseChange,
  type VTicket,
} from "../data";
import { statusTone, typeTone } from "../tone";
import { id8 } from "../ticketThread";
import type { PageProps } from "../Portal";
import { PageHead } from "./parts";
import { NewTicket, type NewTicketPreset } from "./ticketParts";

/* ---------- labels + tones ---------- */

const REL_STATUSES: ReleaseStatus[] = [
  "Planning",
  "Open",
  "On sandbox",
  "Released",
  "Immediate fixes",
];
const relTone = (s: ReleaseStatus): Tone =>
  (
    ({
      Planning: "neutral",
      Open: "navy",
      "On sandbox": "teal",
      Released: "success",
      "Immediate fixes": "warning",
    }) as Record<ReleaseStatus, Tone>
  )[s] ?? "neutral";

const KINDS: ChangeKind[] = ["add", "improve", "fix", "remove"];
const KIND: Record<ChangeKind, [string, Tone]> = {
  add: ["New", "success"],
  improve: ["Improved", "teal"],
  fix: ["Fixed", "warning"],
  remove: ["Removed", "neutral"],
};
const NOTES: Record<VRelease["notesState"], [string, Tone]> = {
  none: ["Not drafted", "neutral"],
  draft: ["Draft ready", "teal"],
  scheduled: ["Scheduled", "navy"],
  sent: ["Sent", "success"],
  na: ["Not needed", "neutral"],
};
const READY: Record<VTicket["ready"], [string, Tone]> = {
  no: ["Not ready", "neutral"],
  sandbox: ["On sandbox", "teal"],
  live: ["Live", "success"],
};
const READY_KEYS: VTicket["ready"][] = ["no", "sandbox", "live"];
const ADVANCE_LABEL: Partial<Record<ReleaseStatus, string>> = {
  Planning: "Open for tickets",
  Open: "Deploy to sandbox",
  "On sandbox": "Put live",
};
const WHEN_LIVE = "When it goes live";
const WHEN_NOW = "Now";

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/* ---------- dates ---------- */

/** "Wed 14 Oct" */
const relDay = (ms: number | null): string =>
  ms == null
    ? ""
    : new Date(ms).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
/** "Fri 9 Oct, 17:00" */
const relDayTime = (ms: number | null): string =>
  ms == null
    ? ""
    : `${relDay(ms)}, ${new Date(ms).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
/** "7 Oct, 16:10" */
const shortStamp = (ms: number | null): string =>
  ms == null
    ? ""
    : `${new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${new Date(
        ms,
      ).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;

const pad = (n: number) => String(n).padStart(2, "0");
const toDateInput = (ms: number | null) => {
  if (ms == null) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const toDateTimeInput = (ms: number | null) => {
  if (ms == null) return "";
  const d = new Date(ms);
  return `${toDateInput(ms)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromDateInput = (v: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : null;
};
const fromDateTimeInput = (v: string): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/* ---------- derived per release ---------- */

/** Next minor version for a project: highest "major.minor" + 0.1, else 1.0. */
function nextVersion(releases: VRelease[], projectId: string): string {
  let best: [number, number] | null = null;
  for (const r of releases) {
    if (r.projectId !== projectId) continue;
    const m = /^(\d+)\.(\d+)$/.exec(r.version);
    if (!m) continue;
    const v: [number, number] = [Number(m[1]), Number(m[2])];
    if (!best || v[0] > best[0] || (v[0] === best[0] && v[1] > best[1])) best = v;
  }
  return best ? `${best[0]}.${best[1] + 1}` : "1.0";
}

const firstName = (s: string) => s.trim().split(/\s+/)[0] || s;

/** When the client should approve by: approve-by, else the go-live day. */
const approveByLabel = (r: VRelease) =>
  r.approveBy != null ? relDayTime(r.approveBy) : relDay(r.goLiveAt) || "the go-live date";

function signShort(r: VRelease, contact: string): string {
  switch (r.signoff) {
    case "pending":
      return `Waiting on ${contact ? firstName(contact) : "the client"}`;
    case "done":
      return r.signedOffBy
        ? `${firstName(r.signedOffBy)}${r.signedOffAt ? ` · ${relDay(r.signedOffAt)}` : ""}`
        : "Signed off";
    case "later":
      return "After sandbox";
    default:
      return r.isFixes ? "On the ticket" : "Not needed";
  }
}
const signDot = (r: VRelease) =>
  r.signoff === "pending"
    ? "var(--status-warning)"
    : r.signoff === "done"
      ? "var(--deep-teal)"
      : "var(--border-strong)";

function signLine(r: VRelease, contact: string): string {
  if (r.isFixes)
    return "Urgent fixes go live once they pass test. The client confirms on the ticket.";
  if (r.signoff === "done")
    return `Signed off by ${r.signedOffBy || "the client"}${
      r.signedOffAt ? ` on ${shortStamp(r.signedOffAt)}` : ""
    }.`;
  if (r.signoff === "pending")
    return `Sent to ${contact || "the client"} for sign-off${
      r.signoffRequestedAt ? ` on ${shortStamp(r.signoffRequestedAt)}` : ""
    }.`;
  if (r.signoff === "later") return "Goes to sandbox for sign-off once the work is built.";
  if (r.status === "Released")
    return `No sign-off needed. Live since ${relDay(r.releasedAt ?? r.goLiveAt)}.`;
  return "This project doesn't need client sign-off. It goes live once it passes on sandbox.";
}

function subLine(r: VRelease): string {
  if (r.note) return r.note;
  switch (r.status) {
    case "Planning":
      return r.goLiveAt ? "Not open for tickets yet" : "Dates to be set";
    case "Open":
      return "Taking tickets until the cut-off";
    case "On sandbox":
      return r.signoff === "pending"
        ? `Sign-off due ${approveByLabel(r)}`
        : r.signoff === "done"
          ? "Signed off, ready to go live"
          : "Passing test on sandbox";
    case "Released":
      return `Live since ${relDay(r.releasedAt ?? r.goLiveAt)}`;
    default:
      return "Urgent fixes, live as soon as they pass test";
  }
}

const goLabel = (r: VRelease) => (r.isFixes ? "On pass" : relDay(r.goLiveAt) || "Not set");
const cutLabel = (r: VRelease) => (r.isFixes ? "None" : relDayTime(r.cutoffAt) || "Not set");

/** Internal detail lines: a leading UPPERCASE word renders as the tag. */
function DetailLines({ text }: { text: string }) {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return null;
  return (
    <>
      {lines.map((l, i) => {
        const m = /^([A-Z][A-Z-]{1,15})\s+(.*)$/.exec(l);
        return (
          <span key={i} className="rel-detail mono">
            {m ? <span className="rel-detail-k">{m[1]}</span> : null}
            <span>{m ? m[2] : l}</span>
          </span>
        );
      })}
    </>
  );
}

function Chip({
  on,
  label,
  count,
  onClick,
}: {
  on: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button className={"rel-chip" + (on ? " is-on" : "")} aria-pressed={on} onClick={onClick}>
      <span>{label}</span>
      <span className="rel-chip-n mono">{count}</span>
    </button>
  );
}

/* ---------- page ---------- */

export function Releases(props: PageProps) {
  if (props.isAdmin) return <AdminReleases {...props} />;
  return <ClientReleases {...props} />;
}

/* ================= admin ================= */

function AdminReleases(props: PageProps) {
  const {
    releases,
    releaseChanges,
    tickets,
    projects,
    scopedProjects,
    pid,
    client,
    focus,
    clearFocus,
    setCrumbTail,
  } = props;
  const [rSel, setRSel] = useState<string | null>(null);
  const [relStatus, setRelStatus] = useState<ReleaseStatus[]>([
    "Planning",
    "Open",
    "On sandbox",
    "Immediate fixes",
  ]);
  const [relProject, setRelProject] = useState("All projects");
  const [adding, setAdding] = useState(false);

  // Arrive on a release from elsewhere (a ticket's "Open →").
  useEffect(() => {
    if (focus?.page !== "releases") return;
    setRSel(focus.id);
    clearFocus();
  }, [focus, clearFocus]);

  const scoped = useMemo(
    () => (pid === "all" ? releases : releases.filter((r) => r.projectId === pid)),
    [releases, pid],
  );
  const selected = rSel ? scoped.find((r) => r.id === rSel) : undefined;

  useEffect(() => {
    setCrumbTail(selected?.name ?? "");
  }, [selected?.name, setCrumbTail]);
  useEffect(() => () => setCrumbTail(""), [setCrumbTail]);

  const ticketsOf = (r: VRelease) => tickets.filter((t) => t.releaseId === r.id);
  const changesOf = (r: VRelease) => releaseChanges.filter((c) => c.releaseId === r.id);

  if (selected) {
    return (
      <ReleaseDetail
        {...props}
        r={selected}
        relTickets={ticketsOf(selected)}
        changes={changesOf(selected)}
        onBack={() => setRSel(null)}
        onDeleted={() => setRSel(null)}
      />
    );
  }

  const shown = scoped.filter(
    (r) =>
      relStatus.includes(r.status) &&
      (relProject === "All projects" || r.projectName === relProject),
  );
  const projectOrder = pid === "all" ? projects : scopedProjects;
  const groups = projectOrder
    .map((p) => {
      const live = scoped
        .filter((r) => r.projectId === p.id && r.status === "Released" && !r.isFixes)
        .sort((a, b) => (b.releasedAt ?? b.createdAt) - (a.releasedAt ?? a.createdAt))[0];
      // Fixes lane last; otherwise newest version first.
      const rows = shown
        .filter((r) => r.projectId === p.id)
        .sort((a, b) =>
          a.isFixes !== b.isFixes
            ? Number(a.isFixes) - Number(b.isFixes)
            : b.version.localeCompare(a.version, undefined, { numeric: true }),
        );
      return { p, live: live?.version ?? "—", rows };
    })
    .filter((g) => g.rows.length > 0);
  const contact = client?.contact ?? "";

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="RELEASES"
        title="Release board"
        sub={`Every ${client?.name ?? "client"} release, by project. Work goes to sandbox first, then live once it passes, or once the client signs it off if the project needs that. Urgent fixes skip the schedule.`}
        actions={
          <Button icon="plus" onClick={() => setAdding(true)} disabled={!scopedProjects.length}>
            New release
          </Button>
        }
      />

      <div className="rel-filters">
        <div className="rel-filter">
          <span className="rel-flabel">Status</span>
          <div className="rel-chips">
            {REL_STATUSES.map((s) => (
              <Chip
                key={s}
                on={relStatus.includes(s)}
                label={s}
                count={scoped.filter((r) => r.status === s).length}
                onClick={() =>
                  setRelStatus((cur) =>
                    cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
                  )
                }
              />
            ))}
          </div>
        </div>
        {pid === "all" ? (
          <div className="rel-filter" style={{ width: 200 }}>
            <span className="rel-flabel">Project</span>
            <Select
              size="sm"
              options={["All projects", ...projects.map((p) => p.name)]}
              value={relProject}
              onChange={(e) => setRelProject(e.target.value)}
            />
          </div>
        ) : null}
        <span className="rel-result">
          Showing {shown.length} of {plural(scoped.length, "release")}
        </span>
      </div>

      <div className="rel-table-wrap">
        <div className="rel-table">
          <div className="rel-grid rel-thead mono">
            <span>Release</span>
            <span>Status</span>
            <span>Go-live</span>
            <span>Cut-off</span>
            <span>Tickets</span>
            <span>Changes</span>
            <span>Sign-off</span>
            <span>Notes</span>
            <span />
          </div>
          {groups.map((g) => (
            <div key={g.p.id}>
              <div className="rel-group">
                <span className="mono rel-group-code">{g.p.code}</span>
                <span className="rel-group-name">{g.p.name}</span>
                <span className="rel-group-cad">{cadenceLine(g.p)}</span>
                <span className="rel-group-live mono">
                  Live: <strong>{g.live}</strong>
                </span>
              </div>
              {g.rows.map((r) => {
                const tk = ticketsOf(r);
                const ready = tk.filter((t) => t.ready !== "no").length;
                return (
                  <button key={r.id} className="rel-grid rel-row" onClick={() => setRSel(r.id)}>
                    <span className="rel-row-main">
                      <span className="rel-row-name">{r.name}</span>
                      <span className="rel-row-sub">{subLine(r)}</span>
                    </span>
                    <span data-label="Status">
                      <Badge tone={relTone(r.status)} dot>
                        {r.status}
                      </Badge>
                    </span>
                    <span className="rel-cell" data-label="Go-live">
                      {goLabel(r)}
                    </span>
                    <span className="rel-cell" data-label="Cut-off">
                      {cutLabel(r)}
                    </span>
                    <span className="rel-tk" data-label="Tickets">
                      <span className="mono">{plural(tk.length, "ticket")}</span>
                      <span className="rel-tk-sub">
                        {r.status === "Released" ? "all live" : `${ready} ready`}
                      </span>
                    </span>
                    <span className="mono rel-cell" data-label="Changes">
                      {changesOf(r).length}
                    </span>
                    <span className="rel-sign" data-label="Sign-off">
                      <span className="ibx-dot" style={{ background: signDot(r) }} />
                      {signShort(r, contact)}
                    </span>
                    <span data-label="Notes">
                      <Badge tone={NOTES[r.notesState][1]}>{NOTES[r.notesState][0]}</Badge>
                    </span>
                    <span className="rel-chev">
                      <Icon name="chevron-right" size={16} />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          {groups.length === 0 ? (
            <div className="rel-empty">
              {scoped.length
                ? "No releases match these filters."
                : "No releases yet. Add one to start planning what goes live."}
            </div>
          ) : null}
        </div>
      </div>

      <ReleaseDialog
        open={adding}
        projects={scopedProjects}
        releases={releases}
        defaultPid={pid !== "all" ? pid : (projects.find((p) => p.name === relProject)?.id ?? "")}
        onClose={() => setAdding(false)}
        onSaved={(id, label) => {
          props.reload();
          setRelStatus((cur) => (cur.includes("Planning") ? cur : [...cur, "Planning"]));
          if (id) setRSel(id);
          props.toast(`${label} added.`);
        }}
      />
    </div>
  );
}

const cadenceLine = (p: VProject) =>
  [p.cadence, p.requiresSignoff ? "client signs off on sandbox" : "no client sign-off"]
    .filter(Boolean)
    .join(" · ");

/* ---------- release detail ---------- */

type DetailTab = "changes" | "tickets" | "notes";

function ReleaseDetail({
  r,
  relTickets,
  changes,
  tickets,
  releases,
  projects,
  client,
  reload,
  toast,
  openItem,
  onBack,
  onDeleted,
}: PageProps & {
  r: VRelease;
  relTickets: VTicket[];
  changes: VReleaseChange[];
  onBack: () => void;
  onDeleted: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>("changes");
  const [kind, setKind] = useState<"all" | ChangeKind>("all");
  const [showDetail, setShowDetail] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changeDlg, setChangeDlg] = useState<{ open: boolean; change: VReleaseChange | null }>({
    open: false,
    change: null,
  });
  const [busy, setBusy] = useState(false);
  const contact = client?.contact ?? "";

  useEffect(() => {
    setTab("changes");
    setKind("all");
  }, [r.id]);

  const run = async (fn: () => Promise<unknown>, msg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      reload();
      toast(msg);
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    "In build",
    "On sandbox",
    r.requiresSignoff ? "Signed off" : "Sign-off (not needed)",
    "Live",
  ];
  const stepIdx =
    r.status === "Released"
      ? 3
      : r.status === "On sandbox"
        ? r.signoff === "done"
          ? 2
          : 1
        : r.status === "Planning"
          ? -1
          : 0;
  const barFor = (i: number) =>
    i < stepIdx
      ? "var(--deep-teal)"
      : i === stepIdx
        ? r.signoff === "pending" && i === 1
          ? "var(--status-warning)"
          : "var(--bright-teal)"
        : "var(--border-slate)";

  const advanceLabel = r.isFixes ? undefined : ADVANCE_LABEL[r.status];
  const blocked =
    r.status === "On sandbox" && r.requiresSignoff && r.signoff !== "done";
  const ready = relTickets.filter((t) => t.ready !== "no").length;
  const notReady = relTickets.filter((t) => t.ready === "no");
  const showNotReady = notReady.length > 0 && r.status !== "Released" && !r.isFixes;
  const cut = cutLabel(r);
  const first = notReady[0];
  const notReadyLine =
    notReady.length === 1 && first
      ? `#${id8(first.id)} ${first.subject} isn't ready. If it's not on sandbox by the cut-off (${cut}), move it to the next release from its Release field.`
      : `${notReady.length} tickets aren't ready: ${notReady
          .map((t) => "#" + id8(t.id))
          .join(", ")}. Anything not on sandbox by ${cut} moves to the next release.`;

  const metaLine = r.isFixes
    ? "Deployed as soon as it passes test · No cut-off"
    : `Goes live ${goLabel(r)} · Cut-off ${cut}`;
  const summaryLine = `${plural(relTickets.length, "ticket")} · ${ready} ready · ${plural(
    changes.length,
    "change",
  )} · Release notes: ${NOTES[r.notesState][0].toLowerCase()}`;

  const advance = () =>
    run(
      () => api.post(`/releases/${r.id}/advance`),
      r.status === "On sandbox"
        ? `${r.name} is live.`
        : r.status === "Open"
          ? `${r.name} deployed to sandbox.`
          : `${r.name} is open for tickets.`,
    );

  // Changes grouped by ticket (in release ticket order), then release-level.
  const pass = (c: VReleaseChange) => kind === "all" || c.kind === kind;
  const groups = [
    ...relTickets.map((t) => ({ key: t.id, ticket: t as VTicket | null })),
    { key: "release", ticket: null as VTicket | null },
  ]
    .map((g) => ({
      ...g,
      items: changes.filter((c) => (c.ticketId ?? null) === (g.ticket?.id ?? null) && pass(c)),
    }))
    .filter((g) => g.items.length > 0);
  // Entries whose ticket has left the release still show, release-level.
  const stray = changes.filter(
    (c) => c.ticketId && !relTickets.some((t) => t.id === c.ticketId) && pass(c),
  );

  const openTicket = (t: VTicket) => openItem("tickets", t.id);

  return (
    <div className="stack-16 rel-detail-page">
      <button className="link-btn" style={{ alignSelf: "flex-start" }} onClick={onBack}>
        ← All releases
      </button>

      <div className="rel-head">
        <div className="rel-head-rule" />
        <div className="rel-head-main">
          <div className="row-8">
            <span className="mono rel-code">{r.projectCode}</span>
            <Badge tone={relTone(r.status)} dot>
              {r.status}
            </Badge>
          </div>
          <h2 className="rel-title">{r.name}</h2>
          <div className="mono rel-meta">{metaLine}</div>
          <div className="rel-summary">{summaryLine}</div>
          <div className="row-8" style={{ marginTop: 6 }}>
            {advanceLabel ? (
              <Button
                size="sm"
                arrow
                disabled={busy || blocked}
                title={blocked ? "Put live unlocks once the client signs this off." : undefined}
                onClick={() => void advance()}
              >
                {advanceLabel}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" icon="pencil" onClick={() => setEditing(true)}>
              Edit release
            </Button>
          </div>
          {blocked ? (
            <div className="rel-blocked">Put live unlocks once the client signs this off.</div>
          ) : null}
        </div>
        <div className="rel-path">
          <div className="rel-flabel">Path to live</div>
          <div className="rel-steps">
            {steps.map((label, i) => (
              <div key={label} className={"rel-step" + (i === stepIdx ? " is-on" : "")}>
                <div
                  className={
                    "rel-step-bar" + (i === 2 && !r.requiresSignoff && i > stepIdx ? " is-dashed" : "")
                  }
                  style={i === 2 && !r.requiresSignoff && i > stepIdx ? undefined : { background: barFor(i) }}
                />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <div className="rel-signline">{signLine(r, contact)}</div>
          {r.signoff === "pending" && r.status === "On sandbox" ? (
            <div className="row-8">
              <Button
                variant="secondary"
                size="sm"
                icon="mail"
                disabled={busy}
                onClick={() =>
                  void run(
                    () => api.post(`/releases/${r.id}/remind`),
                    `Reminder sent to ${contact || "the client"}.`,
                  )
                }
              >
                Send reminder
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void run(
                    () => api.post(`/releases/${r.id}/signoff`),
                    `${r.name} marked signed off.`,
                  )
                }
              >
                Mark signed off
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Tabs
        items={[
          { value: "changes", label: "Changes", count: changes.length || undefined },
          { value: "tickets", label: "Tickets", count: relTickets.length || undefined },
          { value: "notes", label: "Release notes" },
        ]}
        value={tab}
        onChange={(v) => setTab(v as DetailTab)}
      />

      {tab === "changes" ? (
        <div className="stack-12">
          <div className="rel-kindbar">
            <div className="rel-chips">
              <Chip
                on={kind === "all"}
                label="All"
                count={changes.length}
                onClick={() => setKind("all")}
              />
              {KINDS.map((k) => (
                <Chip
                  key={k}
                  on={kind === k}
                  label={KIND[k][0]}
                  count={changes.filter((c) => c.kind === k).length}
                  onClick={() => setKind(k)}
                />
              ))}
            </div>
            <label className="rel-check">
              <input
                type="checkbox"
                checked={showDetail}
                onChange={(e) => setShowDetail(e.target.checked)}
              />
              Show internal detail
            </label>
            <span style={{ flex: 1 }} />
            <Button
              size="sm"
              variant="secondary"
              icon="plus"
              onClick={() => setChangeDlg({ open: true, change: null })}
            >
              Add change
            </Button>
          </div>
          {showNotReady ? (
            <div className="rel-warn">
              <Icon name="triangle-alert" size={15} />
              <span>{notReadyLine}</span>
            </div>
          ) : null}
          {groups.map((g) => (
            <div key={g.key} className="rel-cgroup">
              {g.ticket ? (
                <button className="rel-cgroup-head" onClick={() => openTicket(g.ticket as VTicket)}>
                  <span className="mono rel-cgroup-ref">#{id8(g.ticket.id)}</span>
                  <span className="rel-cgroup-title">{g.ticket.subject}</span>
                  <span className="rel-cgroup-meta">
                    {g.ticket.category} · {g.ticket.author}
                  </span>
                  <span className="row-6" style={{ marginLeft: "auto" }}>
                    <Badge tone={statusTone(g.ticket.status)} dot>
                      {g.ticket.status}
                    </Badge>
                    <Badge tone={READY[g.ticket.ready][1]}>{READY[g.ticket.ready][0]}</Badge>
                  </span>
                </button>
              ) : (
                <div className="rel-cgroup-head is-static">
                  <span className="mono rel-cgroup-ref">Release</span>
                  <span className="rel-cgroup-title">Not tied to a ticket</span>
                  <span className="rel-cgroup-meta">Release-level</span>
                </div>
              )}
              {g.items.map((c) => (
                <ChangeRow
                  key={c.id}
                  c={c}
                  showDetail={showDetail}
                  onEdit={() => setChangeDlg({ open: true, change: c })}
                />
              ))}
            </div>
          ))}
          {stray.length ? (
            <div className="rel-cgroup">
              <div className="rel-cgroup-head is-static">
                <span className="mono rel-cgroup-ref">Moved</span>
                <span className="rel-cgroup-title">Tickets no longer in this release</span>
              </div>
              {stray.map((c) => (
                <ChangeRow
                  key={c.id}
                  c={c}
                  showDetail={showDetail}
                  onEdit={() => setChangeDlg({ open: true, change: c })}
                />
              ))}
            </div>
          ) : null}
          {groups.length === 0 && stray.length === 0 ? (
            <div className="rel-card-empty">
              {changes.length
                ? "No changes of that kind."
                : "No changes yet. Add one, or link a ticket from its Release field."}
            </div>
          ) : null}
          <span className="rel-foot">
            <Icon name="lock" size={12} />
            Mono lines are internal. Agile Software only, never sent to the client.
          </span>
        </div>
      ) : null}

      {tab === "tickets" ? (
        <ReleaseTickets
          r={r}
          relTickets={relTickets}
          tickets={tickets}
          changes={changes}
          onOpen={openTicket}
          run={run}
        />
      ) : null}

      {tab === "notes" ? (
        <ReleaseNotes r={r} changes={changes} clientId={r.clientId} run={run} busy={busy} />
      ) : null}

      <ReleaseDialog
        open={editing}
        release={r}
        projects={projects}
        releases={releases}
        defaultPid={r.projectId}
        onClose={() => setEditing(false)}
        onSaved={(_id, label) => {
          reload();
          toast(`${label} saved.`);
        }}
        onDeleted={() => {
          reload();
          onDeleted();
          toast(`${r.name} deleted.`);
        }}
      />
      <ChangeDialog
        open={changeDlg.open}
        change={changeDlg.change}
        release={r}
        relTickets={relTickets}
        onClose={() => setChangeDlg({ open: false, change: null })}
        onSaved={(msg) => {
          reload();
          toast(msg);
        }}
      />
    </div>
  );
}

function ChangeRow({
  c,
  showDetail,
  onEdit,
}: {
  c: VReleaseChange;
  showDetail: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="rel-change">
      <span>
        <Badge tone={KIND[c.kind][1]}>{KIND[c.kind][0]}</Badge>
      </span>
      <span className="rel-change-body">
        <span className="rel-change-sum">{c.summary}</span>
        {showDetail ? <DetailLines text={c.details} /> : null}
      </span>
      <span className="rel-change-edit">
        <IconButton icon="pencil" label="Edit change" variant="ghost" onClick={onEdit} />
      </span>
    </div>
  );
}

/* ---------- tickets tab ---------- */

function ReleaseTickets({
  r,
  relTickets,
  tickets,
  changes,
  onOpen,
  run,
}: {
  r: VRelease;
  relTickets: VTicket[];
  tickets: VTicket[];
  changes: VReleaseChange[];
  onOpen: (t: VTicket) => void;
  run: (fn: () => Promise<unknown>, msg: string) => Promise<void>;
}) {
  const patch = (t: VTicket, body: Record<string, unknown>) =>
    api.patch(`/projects/${t.projectId}/tickets/${t.id}`, body);
  const PICK = "Add a ticket to this release…";
  const candidates = tickets.filter(
    (t) => t.projectId === r.projectId && t.releaseId !== r.id && t.status !== "Closed",
  );
  const label = (t: VTicket) => `#${id8(t.id)} ${t.subject}`;
  const canAdd = r.status !== "Released";

  return (
    <div className="stack-12">
      {canAdd && candidates.length ? (
        <div style={{ maxWidth: 460 }}>
          <Select
            size="sm"
            options={[PICK, ...candidates.map(label)]}
            value={PICK}
            onChange={(e) => {
              const t = candidates.find((x) => label(x) === e.target.value);
              if (t)
                void run(
                  () => patch(t, { releaseId: r.id }),
                  `#${id8(t.id)} added to ${r.name}`,
                );
            }}
          />
        </div>
      ) : null}
      <div className="rel-list">
        {relTickets.map((t) => (
          <div
            key={t.id}
            className="rel-trow"
            role="button"
            tabIndex={0}
            onClick={() => onOpen(t)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onOpen(t);
            }}
          >
            <span className="row-8">
              <span className="mono rel-tid">#{id8(t.id)}</span>
              <Badge tone={typeTone(t.category)}>{t.category}</Badge>
              <span style={{ flex: 1 }} />
              <span className="rel-tmeta">
                {plural(changes.filter((c) => c.ticketId === t.id).length, "change")}
              </span>
              <Badge tone={statusTone(t.status)} dot>
                {t.status}
              </Badge>
              {/* Ready state: set by hand; going live moves On sandbox → Live. */}
              <span
                className="rel-ready"
                data-tone={READY[t.ready][1]}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <select
                  aria-label="Ready"
                  value={t.ready}
                  onChange={(e) =>
                    void run(
                      () => patch(t, { ready: e.target.value }),
                      `#${id8(t.id)} · ${READY[e.target.value as VTicket["ready"]][0]}`,
                    )
                  }
                >
                  {READY_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {READY[k][0]}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down" size={12} />
              </span>
              <span onClick={(e) => e.stopPropagation()}>
                <IconButton
                  icon="x"
                  label="Remove from release"
                  variant="ghost"
                  onClick={() =>
                    void run(
                      () => patch(t, { releaseId: null }),
                      `#${id8(t.id)} removed from ${r.name}`,
                    )
                  }
                />
              </span>
            </span>
            <span className="rel-tsubject">{t.subject}</span>
            <span className="rel-tmeta">
              Raised by {t.author} · {t.assignee}
            </span>
          </div>
        ))}
        {relTickets.length === 0 ? (
          <div className="rel-empty">No tickets yet. Link one from a ticket's Release field.</div>
        ) : null}
      </div>
    </div>
  );
}

/* ---------- release notes tab ---------- */

interface NotesDoc {
  title: string;
  intro: string;
  sections: { label: string; lines: string[] }[];
  text: string;
}
interface ClientUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

function ReleaseNotes({
  r,
  changes,
  clientId,
  run,
  busy,
}: {
  r: VRelease;
  changes: VReleaseChange[];
  clientId: string;
  run: (fn: () => Promise<unknown>, msg: string) => Promise<void>;
  busy: boolean;
}) {
  const [doc, setDoc] = useState<NotesDoc | null>(null);
  const [people, setPeople] = useState<ClientUser[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [when, setWhen] = useState(r.notesSend === "now" ? WHEN_NOW : WHEN_LIVE);
  const changeKey = changes.map((c) => `${c.id}:${c.kind}:${c.summary}`).join("|");

  // Built server-side from the client-facing summaries only.
  useEffect(() => {
    let live = true;
    api
      .get<{ notes: NotesDoc }>(`/releases/${r.id}/notes`)
      .then((x) => live && setDoc(x.notes))
      .catch(() => live && setDoc(null));
    return () => {
      live = false;
    };
  }, [r.id, r.status, r.goLiveAt, changeKey]);

  useEffect(() => {
    let live = true;
    api
      .get<{ users: ClientUser[] }>(`/clients/${clientId}/users`)
      .then((x) => {
        if (!live) return;
        const list = x.users.filter((u) => u.role === "client");
        setPeople(list);
        const saved = r.notesRecipients.filter((e) => list.some((u) => u.email === e));
        setPicked(saved.length ? saved : list.map((u) => u.email));
      })
      .catch(() => live && setPeople([]));
    return () => {
      live = false;
    };
  }, [clientId, r.id, r.notesRecipients]);

  const released = r.status === "Released";
  const locked = r.notesState === "sent" || r.notesState === "na";
  const now = released || when === WHEN_NOW;
  const action =
    r.notesState === "sent"
      ? "Sent"
      : r.notesState === "na"
        ? "Not needed"
        : r.notesState === "scheduled" && !now
          ? "Scheduled"
          : now
            ? "Send now"
            : "Schedule";
  const initialsOf = (u: ClientUser) =>
    (u.name || u.email)
      .split(/[\s@.]+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <div className="rel-notes">
      <div className="rel-notes-doc">
        <div className="rel-notes-rule" />
        <div className="rel-notes-eyebrow mono">Release notes · {r.projectName}</div>
        <h3 className="rel-notes-title">{doc?.title ?? r.name}</h3>
        <p className="rel-notes-intro">{doc?.intro ?? ""}</p>
        {(doc?.sections ?? []).map((s) => (
          <div key={s.label} className="rel-stack-6">
            <div className="rel-notes-sec">{s.label}</div>
            {s.lines.map((l, i) => (
              <div key={i} className="rel-notes-line">
                <span className="rel-notes-arrow">→</span>
                <span>{l}</span>
              </div>
            ))}
          </div>
        ))}
        {doc && !doc.sections.length ? (
          <p className="rel-notes-empty">No changes yet. Add them on the Changes tab.</p>
        ) : null}
        <p className="rel-notes-foot">
          Questions? Reply to this email or raise a ticket in your portal.
        </p>
      </div>

      <div className="rel-send">
        <div className="row-8 between">
          <span className="rel-send-title">Send to client</span>
          <Badge tone={NOTES[r.notesState][1]}>{NOTES[r.notesState][0]}</Badge>
        </div>
        <div className="stack-8">
          <span className="rel-flabel">Recipients</span>
          {people.map((u) => (
            <label key={u.id} className="rel-person">
              <input
                type="checkbox"
                disabled={locked}
                checked={picked.includes(u.email)}
                onChange={(e) =>
                  setPicked((cur) =>
                    e.target.checked ? [...cur, u.email] : cur.filter((x) => x !== u.email),
                  )
                }
              />
              <span className="rel-person-av mono">{initialsOf(u)}</span>
              <span className="rel-person-text">
                <span className="rel-person-name">{u.name || u.email}</span>
                <span className="rel-person-mail">{u.email}</span>
              </span>
            </label>
          ))}
          {people.length === 0 ? (
            <span className="hint">No client users yet. Add them in Team &amp; access.</span>
          ) : null}
        </div>
        <div className="rel-stack-6">
          <span className="rel-flabel">When</span>
          <Select
            size="sm"
            options={released ? [WHEN_NOW] : [WHEN_LIVE, WHEN_NOW]}
            value={released ? WHEN_NOW : when}
            disabled={locked}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          arrow
          disabled={
            locked ||
            busy ||
            !picked.length ||
            !changes.length ||
            (r.notesState === "scheduled" && !now)
          }
          onClick={() =>
            void run(
              () =>
                api.post(`/releases/${r.id}/notes`, {
                  send: now ? "now" : "when-live",
                  recipients: picked,
                }),
              now
                ? `Release notes sent to ${plural(picked.length, "person")
                    .replace("persons", "people")} at ${r.projectName}.`
                : `Release notes will go out when ${r.name} goes live.`,
            )
          }
        >
          {action}
        </Button>
        <span className="rel-send-hint">
          Built from the change summaries. Internal detail is never included.
        </span>
      </div>
    </div>
  );
}

/* ---------- dialogs ---------- */

function ReleaseDialog({
  open,
  release,
  projects,
  releases,
  defaultPid,
  onClose,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  release?: VRelease;
  projects: VProject[];
  releases: VRelease[];
  defaultPid: string;
  onClose: () => void;
  onSaved: (id: string | null, label: string) => void;
  onDeleted?: () => void;
}) {
  const editing = !!release;
  const blank = () => {
    const p = projects.find((x) => x.id === defaultPid) ?? projects[0];
    return {
      projectId: p?.id ?? "",
      version: p ? nextVersion(releases, p.id) : "1.0",
      note: "",
      go: "",
      cut: "",
      approve: "",
      sandbox: "",
      status: "Planning" as ReleaseStatus,
    };
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setConfirmDelete(false);
    setF(
      release
        ? {
            projectId: release.projectId,
            version: release.version,
            note: release.note,
            go: toDateInput(release.goLiveAt),
            cut: toDateTimeInput(release.cutoffAt),
            approve: toDateTimeInput(release.approveBy),
            sandbox: release.sandboxUrl,
            status: release.status,
          }
        : blank(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, release?.id]);

  const project = projects.find((p) => p.id === f.projectId);
  const version = f.version.trim();
  const clash = releases.some(
    (x) => x.projectId === f.projectId && x.version === version && x.id !== release?.id,
  );
  const invalid = !project || !version || clash;
  const statusEditable =
    editing &&
    !!release &&
    (["Planning", "Open", "Immediate fixes"] as ReleaseStatus[]).includes(release.status);

  const save = async () => {
    if (invalid || busy) return;
    setBusy(true);
    setError("");
    const label = `${project?.name ?? ""} ${version === FIXES_VERSION ? "· immediate fixes" : version}`;
    try {
      const fields = {
        version,
        name: f.note.trim(),
        goLiveAt: fromDateInput(f.go),
        cutoffAt: fromDateTimeInput(f.cut),
        approveBy: fromDateTimeInput(f.approve),
        sandboxUrl: f.sandbox.trim(),
      };
      if (release) {
        await api.patch(`/releases/${release.id}`, {
          ...fields,
          ...(statusEditable && f.status !== release.status ? { status: f.status } : {}),
        });
        onSaved(release.id, label);
      } else {
        const r = await api.post<{ release: { id: string } }>("/releases", {
          projectId: f.projectId,
          ...fields,
        });
        onSaved(r.release.id, label);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the release. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!release || busy) return;
    setBusy(true);
    try {
      await api.del(`/releases/${release.id}`);
      onClose();
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete the release. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const signNote = project
    ? project.requiresSignoff
      ? `${project.name} needs client sign-off on sandbox before go-live.`
      : `${project.name} doesn't need client sign-off.`
    : "";

  return (
    <Dialog
      open={open}
      title={editing ? "Edit release" : "Add a release"}
      description={
        editing
          ? "Dates and details. Move it along the path to live from the release page."
          : "Releases belong to one project and take its next version number."
      }
      accent
      width={480}
      onClose={onClose}
      footer={
        <div className="row-8" style={{ width: "100%" }}>
          {editing ? (
            confirmDelete ? (
              <span className="row-8 rel-del">
                <span>Delete {release?.name}?</span>
                <button className="rel-del-btn" onClick={() => void remove()} disabled={busy}>
                  Delete
                </button>
                <button className="link-btn" onClick={() => setConfirmDelete(false)}>
                  Keep it
                </button>
              </span>
            ) : (
              <button className="rel-del-btn" onClick={() => setConfirmDelete(true)}>
                Delete…
              </button>
            )
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button arrow disabled={invalid || busy} onClick={() => void save()}>
            {busy ? "Saving…" : editing ? "Save release" : "Add release"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <div className="rel-dlg-row">
          <Field label="Project">
            <Select
              options={projects.map((p) => p.name)}
              value={project?.name ?? ""}
              disabled={editing}
              onChange={(e) => {
                const p = projects.find((x) => x.name === e.target.value);
                if (p) setF((s) => ({ ...s, projectId: p.id, version: nextVersion(releases, p.id) }));
              }}
            />
          </Field>
          <Field label="Version">
            <Input
              value={f.version}
              aria-invalid={clash}
              className={clash ? "is-invalid" : undefined}
              onChange={(e) => setF((s) => ({ ...s, version: e.target.value }))}
            />
          </Field>
        </div>
        {clash ? (
          <span className="nt-error">
            {project?.name} already has a {version} release. Pick another version.
          </span>
        ) : null}
        <div className="grid-2">
          <Field label="Go-live">
            <Input
              type="date"
              value={f.go}
              onChange={(e) => setF((s) => ({ ...s, go: e.target.value }))}
            />
          </Field>
          <Field label="Cut-off">
            <Input
              type="datetime-local"
              value={f.cut}
              onChange={(e) => setF((s) => ({ ...s, cut: e.target.value }))}
            />
          </Field>
        </div>
        {editing ? (
          <>
            <div className="grid-2">
              <Field label="Approve by" hint="Blank uses the go-live date.">
                <Input
                  type="datetime-local"
                  value={f.approve}
                  onChange={(e) => setF((s) => ({ ...s, approve: e.target.value }))}
                />
              </Field>
              {statusEditable ? (
                <Field label="Status">
                  <Select
                    options={["Planning", "Open", "Immediate fixes"]}
                    value={f.status}
                    onChange={(e) =>
                      setF((s) => ({ ...s, status: e.target.value as ReleaseStatus }))
                    }
                  />
                </Field>
              ) : null}
            </div>
            <Field label="Sandbox URL" hint="Where the client checks the release.">
              <Input
                placeholder="sandbox.trade.example.co.uk"
                value={f.sandbox}
                onChange={(e) => setF((s) => ({ ...s, sandbox: e.target.value }))}
              />
            </Field>
            <Field label="Note" hint="Optional line under the name on the board.">
              <Input
                placeholder="Picks up anything that misses 2.4"
                value={f.note}
                onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))}
              />
            </Field>
          </>
        ) : null}
        <span className="hint">{signNote}</span>
        {error ? <div className="nt-error">{error}</div> : null}
      </div>
    </Dialog>
  );
}

function ChangeDialog({
  open,
  change,
  release,
  relTickets,
  onClose,
  onSaved,
}: {
  open: boolean;
  change: VReleaseChange | null;
  release: VRelease;
  relTickets: VTicket[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const NONE = "Not tied to a ticket";
  const label = (t: VTicket) => `#${id8(t.id)} ${t.subject}`;
  const [f, setF] = useState({ kind: "improve" as ChangeKind, summary: "", details: "", ticket: NONE });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const t = change?.ticketId ? relTickets.find((x) => x.id === change.ticketId) : undefined;
    setF({
      kind: change?.kind ?? "improve",
      summary: change?.summary ?? "",
      details: change?.details ?? "",
      ticket: t ? label(t) : NONE,
    });
    setTried(false);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, change?.id]);

  const missing = !f.summary.trim();
  const ticketId = relTickets.find((t) => label(t) === f.ticket)?.id ?? null;

  const save = async () => {
    setTried(true);
    if (missing || busy) return;
    setBusy(true);
    setError("");
    const body = { kind: f.kind, summary: f.summary.trim(), details: f.details, ticketId };
    try {
      if (change) await api.patch(`/release-changes/${change.id}`, body);
      else await api.post(`/releases/${release.id}/changes`, body);
      onSaved(change ? "Change saved." : `Change added to ${release.name}.`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the change. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!change || busy) return;
    setBusy(true);
    try {
      await api.del(`/release-changes/${change.id}`);
      onSaved("Change deleted.");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete the change. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={change ? "Edit change" : "Add a change"}
      description="The summary goes in the client's release notes. Detail stays internal."
      accent
      width={480}
      onClose={onClose}
      footer={
        <div className="row-8" style={{ width: "100%" }}>
          {change ? (
            <button className="rel-del-btn" onClick={() => void remove()} disabled={busy}>
              Delete…
            </button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button arrow disabled={busy} onClick={() => void save()}>
            {change ? "Save change" : "Add change"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <Field label="Kind">
          <div className="rel-chips">
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                className={"rel-chip" + (f.kind === k ? " is-on" : "")}
                aria-pressed={f.kind === k}
                onClick={() => setF((s) => ({ ...s, kind: k }))}
              >
                {KIND[k][0]}
              </button>
            ))}
          </div>
        </Field>
        <div className="nt-subject">
          <Field label="Summary" required hint="One plain sentence the client will read.">
            <Input
              placeholder="Trade search shows stock on hand next to each product."
              value={f.summary}
              aria-invalid={tried && missing}
              className={tried && missing ? "is-invalid" : undefined}
              onChange={(e) => setF((s) => ({ ...s, summary: e.target.value }))}
            />
          </Field>
          {tried && missing ? (
            <span className="nt-error">Add a summary the client will read.</span>
          ) : null}
        </div>
        <Field
          label="Internal detail"
          hint="One line each, e.g. AMEND batch size 250 → 100. Never sent to the client."
        >
          <Input
            multiline
            rows={3}
            value={f.details}
            onChange={(e) => setF((s) => ({ ...s, details: e.target.value }))}
          />
        </Field>
        <Field label="Ticket">
          <Select
            options={[NONE, ...relTickets.map(label)]}
            value={f.ticket}
            onChange={(e) => setF((s) => ({ ...s, ticket: e.target.value }))}
          />
        </Field>
        {error ? <div className="nt-error">{error}</div> : null}
      </div>
    </Dialog>
  );
}

/* ================= client ================= */

function ClientReleases({
  releases,
  releaseChanges,
  projects,
  pid,
  reload,
  toast,
  focus,
  clearFocus,
}: PageProps) {
  const [pastOpen, setPastOpen] = useState<string | null>(null);
  // Opened from Home / To do: releases waiting on sign-off already lead the
  // page, so just consume the focus.
  useEffect(() => {
    if (focus?.page === "releases") clearFocus();
  }, [focus, clearFocus]);
  const [preset, setPreset] = useState<NewTicketPreset | null>(null);
  const [busy, setBusy] = useState(false);

  const scoped = (pid === "all" ? releases : releases.filter((r) => r.projectId === pid)).filter(
    (r) => !r.isFixes,
  );
  const changesOf = (r: VRelease) => releaseChanges.filter((c) => c.releaseId === r.id);
  const deKind = (c: VReleaseChange) => (
    <div key={c.id} className="rel-cl-change">
      <span>
        <Badge tone={KIND[c.kind][1]}>{KIND[c.kind][0]}</Badge>
      </span>
      <span>{c.summary}</span>
    </div>
  );

  const toTest = scoped.filter((r) => r.status === "On sandbox" && r.requiresSignoff);
  const upcoming = scoped
    .filter((r) => r.status === "Open" || r.status === "Planning")
    .sort((a, b) => (a.goLiveAt ?? Infinity) - (b.goLiveAt ?? Infinity));
  const past = scoped
    .filter((r) => r.status === "Released")
    .sort((a, b) => (b.releasedAt ?? b.goLiveAt ?? 0) - (a.releasedAt ?? a.goLiveAt ?? 0));

  const act = async (r: VRelease, path: "approve" | "unapprove", msg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/releases/${r.id}/${path}`);
      reload();
      if (msg) toast(msg);
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const href = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="RELEASES"
        title="Releases"
        sub="What's changing in your software, and when. New work goes to a sandbox first so you can check it before it goes live."
      />

      {toTest.map((r) => {
        const done = r.signoff === "done";
        // Changes from tickets that aren't on sandbox yet aren't there to test.
        const cs = changesOf(r).filter((c) => !c.ticketId || c.ticketReady !== "no");
        const go = relDay(r.goLiveAt) || "once it's scheduled";
        return (
          <div key={r.id} className="rel-hero">
            <div className="rel-head-rule" />
            <div className="stack-12" style={{ minWidth: 0 }}>
              <div className="row-8">
                <span className="rel-flabel">{done ? "Signed off" : "Ready for you to test"}</span>
                <Badge tone={done ? "success" : "teal"} dot>
                  {done ? "Approved" : "On sandbox"}
                </Badge>
              </div>
              <h2 className="rel-title">{r.name}</h2>
              {r.sandboxUrl ? (
                <a
                  className="rel-sandbox mono"
                  href={href(r.sandboxUrl)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {r.sandboxUrl.replace(/^https?:\/\//i, "")}
                  <Icon name="external-link" size={12} />
                </a>
              ) : null}
              <div className="stack-8" style={{ marginTop: 4 }}>
                {cs.map(deKind)}
                {cs.length === 0 ? (
                  <span className="hint">We'll list what changed here shortly.</span>
                ) : null}
              </div>
            </div>
            <div className="rel-hero-side">
              {!done ? (
                <>
                  <div className="rel-hero-title">Check it, then approve</div>
                  <div className="rel-hero-copy">
                    Approve by <strong>{approveByLabel(r)}</strong> and it goes live{" "}
                    <strong>{go}</strong>. If something's wrong, report it and we'll fix it in
                    this release.
                  </div>
                  <Button
                    arrow
                    className="rel-full"
                    disabled={busy}
                    onClick={() => void act(r, "approve", `Thanks. ${r.name} goes live ${go}.`)}
                  >
                    Approve for go-live
                  </Button>
                  <Button
                    variant="secondary"
                    icon="message-square"
                    className="rel-full"
                    onClick={() =>
                      setPreset({
                        subject: `Problem on ${r.name} sandbox: `,
                        projectId: r.projectId,
                        releaseId: r.id,
                      })
                    }
                  >
                    Report a problem
                  </Button>
                </>
              ) : (
                <>
                  <div className="rel-hero-title row-8">
                    <span className="rel-hero-check">
                      <Icon name="check" size={18} />
                    </span>
                    Approved
                  </div>
                  <div className="rel-hero-copy">
                    {signLine(r, "")} It goes live {go}, and we'll email the release notes when
                    it does.
                  </div>
                  <button
                    className="link-btn"
                    style={{ alignSelf: "flex-start" }}
                    disabled={busy}
                    onClick={() => void act(r, "unapprove", "Approval undone.")}
                  >
                    Undo approval
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}

      <div className="rel-cl-grid">
        <div className="rel-cl-card">
          <div className="rel-cl-head">Coming up</div>
          {upcoming.map((r) => (
            <div key={r.id} className="rel-cl-row">
              <div className="rel-cl-text">
                <span className="rel-cl-name">{r.name}</span>
                <span className="rel-cl-line">
                  {r.status === "Planning" || !r.goLiveAt
                    ? `Dates to be set · ${plural(changesOf(r).length, "change")}`
                    : `Goes live ${relDay(r.goLiveAt)} · ${plural(changesOf(r).length, "change")}`}
                </span>
              </div>
              <Badge tone={relTone(r.status)} dot>
                {r.status}
              </Badge>
            </div>
          ))}
          {upcoming.length === 0 ? <div className="rel-empty">Nothing planned right now.</div> : null}
        </div>
        <div className="rel-cl-card">
          <div className="rel-cl-head">Released</div>
          {past.map((r) => {
            const isOpen = pastOpen === r.id;
            const cs = changesOf(r);
            return (
              <div key={r.id} className="rel-cl-past">
                <button
                  className="rel-cl-row is-button"
                  aria-expanded={isOpen}
                  onClick={() => setPastOpen(isOpen ? null : r.id)}
                >
                  <div className="rel-cl-text">
                    <span className="rel-cl-name">{r.name}</span>
                    <span className="rel-cl-line">
                      Live {relDay(r.releasedAt ?? r.goLiveAt)} · {plural(cs.length, "change")}
                    </span>
                  </div>
                  <span className="rel-cl-cta">{isOpen ? "Hide notes" : "Read notes →"}</span>
                </button>
                {isOpen ? (
                  <div className="rel-cl-notes">
                    {cs.map(deKind)}
                    {cs.length === 0 ? <span className="hint">No notes for this one.</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
          {past.length === 0 ? <div className="rel-empty">Nothing released yet.</div> : null}
        </div>
      </div>

      <NewTicket
        open={!!preset}
        isAdmin={false}
        projects={projects}
        defaultPid={preset?.projectId ?? pid}
        preset={preset}
        onClose={() => setPreset(null)}
        onCreated={() => {
          reload();
          toast("Thanks. We'll look at it and fix it in this release.");
        }}
      />
    </div>
  );
}
